import React, { useState } from 'react';
import Card from '@/components/ui/Card';
import { AggregateBuildUpAnalysis } from '@/services/api';
import PatternRow from './PatternRow';

const BuildUpAnalysisResults: React.FC<{ analysis: AggregateBuildUpAnalysis }> = ({ analysis }) => {
  const [activeSection, setActiveSection] = useState<'scored' | 'conceded'>('scored');
  const patterns = activeSection === 'scored' ? analysis.scoredPatterns : analysis.concededPatterns;
  const stats = activeSection === 'scored' ? analysis.scoredStats : analysis.concededStats;

  const sortedZones = Object.entries(stats.commonStartZones || {}).sort((a, b) => b[1] - a[1]);
  const sortedFlanks = Object.entries(stats.commonFlanks || {}).sort((a, b) => b[1] - a[1]);
  const maxZoneCount = sortedZones.length > 0 ? sortedZones[0][1] : 1;
  const maxFlankCount = sortedFlanks.length > 0 ? sortedFlanks[0][1] : 1;

  return (
    <Card className="space-y-6">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
          <span>Goal Build-Up Patterns</span>
        </h2>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-center">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{analysis.matchesAnalyzed}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Matches Analyzed</div>
        </div>
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
          <div className="text-2xl font-bold text-green-700 dark:text-green-400">{analysis.totalGoalsScored}</div>
          <div className="text-sm text-green-600 dark:text-green-500">Goals Scored</div>
        </div>
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
          <div className="text-2xl font-bold text-red-700 dark:text-red-400">{analysis.totalGoalsConceded}</div>
          <div className="text-sm text-red-600 dark:text-red-500">Goals Conceded</div>
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setActiveSection('scored')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
            activeSection === 'scored'
              ? 'bg-green-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          Scoring Patterns ({analysis.scoredPatterns.length})
        </button>
        <button
          onClick={() => setActiveSection('conceded')}
          className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
            activeSection === 'conceded'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          Conceding Patterns ({analysis.concededPatterns.length})
        </button>
      </div>

      {/* Patterns list */}
      <div className="space-y-3">
        {patterns.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-6">
            No {activeSection === 'scored' ? 'scoring' : 'conceding'} patterns found.
          </p>
        ) : (
          patterns.map((pattern, idx) => (
            <PatternRow key={pattern.pattern} pattern={pattern} index={idx} />
          ))
        )}
      </div>

      {/* Aggregate stats */}
      {patterns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {/* Avg sequence length */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Avg Sequence Length</div>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{stats.avgSeqLen.toFixed(1)} events</div>
          </div>

          {/* Common start zones */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Start Zones</div>
            <div className="space-y-1">
              {sortedZones.slice(0, 3).map(([zone, count]) => (
                <div key={zone} className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(count / maxZoneCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-400 w-24 text-right">
                    {zone.replace(/-/g, ' ')} ({count})
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Common flanks */}
          <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Attack Flanks</div>
            <div className="space-y-1">
              {sortedFlanks.map(([flank, count]) => (
                <div key={flank} className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full"
                      style={{ width: `${(count / maxFlankCount) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 dark:text-gray-400 w-16 text-right">
                    {flank} ({count})
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default BuildUpAnalysisResults;
